
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Token from "../../database/Token";
import { toastCfg } from "../../data/toastCfg";

export default function RequestLeads() {
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("id") || searchParams.get("lead_id");
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(Boolean(leadId));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fetchLead = useCallback(async () => {
    if (!leadId) {
      setError("No lead ID was provided. Open this page with ?id=LEAD_ID.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await Token.get(`/leads/request-transfer/${leadId}`);
      setLead(response.data.data);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load this lead.");
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const requestLead = async () => {
    setSubmitting(true);

    try {
      const response = await Token.post("/leads/request-transfer", {
        lead_id: lead.id,
      });
      toast.success(response.data.message, toastCfg);
      await fetchLead();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to request this lead.",
        toastCfg
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <ToastContainer />
      <div className="rounded-3xl border border-surface-border dark:border-surface-darkBorder bg-white dark:bg-surface-darkCard p-6 shadow-sm">
        <h1 className="text-2xl font-black text-gray-800 dark:text-white">
          Request Lead
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Eligible requests are sent to a superadmin or branch manager for approval.
        </p>

        {loading && <p className="mt-6">Loading lead...</p>}

        {!loading && error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {!loading && lead && (
          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-1 gap-4 rounded-2xl bg-surface-soft dark:bg-surface-darkMuted p-5 sm:grid-cols-2">
              <div>
                <p className="text-xs text-gray-500">Lead</p>
                <p className="font-bold text-gray-800 dark:text-white">
                  {lead.name} ({lead.lead_id})
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Current assignee</p>
                <p className="font-bold text-gray-800 dark:text-white">
                  {lead.assigned_to?.name || "Unassigned"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Company</p>
                <p className="font-semibold">{lead.company || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Lead age</p>
                <p className="font-semibold">
                  {lead.days_old} days (must be over {lead.threshold_days})
                </p>
              </div>
            </div>

            <div
              className={`rounded-2xl border p-4 ${
                lead.can_request
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
            >
              {lead.message}
            </div>

            <button
              type="button"
              onClick={requestLead}
              disabled={!lead.can_request || submitting}
              className="h-12 w-full rounded-2xl bg-primary-500 px-5 font-bold text-white hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Transfer Request"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
